import apiService from './apiService';

/**
 * Staff notifications behind the bell (contract livecenter §5, "Benachrichtigungen").
 *
 * The backend groups them: one open row per (kind, contact | call) is counted
 * up (`count`) instead of adding a new row, so "Anna schreibt" stays ONE entry
 * that grows. `notification.upsert` carries the full object, `notification.read`
 * `{ids, all}`.
 */

export type NotificationKind =
  | 'handoff_requested'
  | 'customer_waiting'
  | 'handoff_unanswered'
  | 'callback_requested'
  | 'callback_window_closing'
  | 'whatsapp_activity'
  | 'call_live'
  | 'call_transfer'
  /** Meta blocked one of the salon's WhatsApp numbers (delivery contract 30.09.): belongs to no contact or call. */
  | 'whatsapp_channel_blocked';

/** Kinds the contract marks `urgent=true` — the bell rings the urgent chime for these. */
export const URGENT_NOTIFICATION_KINDS: ReadonlySet<NotificationKind> = new Set<NotificationKind>([
  'handoff_requested',
  'customer_waiting',
  'handoff_unanswered',
  'callback_requested',
  'callback_window_closing',
  'whatsapp_channel_blocked',
]);

export interface StaffNotification {
  id: number;
  kind: NotificationKind;
  urgent: boolean;
  title: string;
  preview: string | null;
  /** How many events this grouped entry stands for ("3 neue Nachrichten"). */
  count: number;
  contact_key: string | null;
  call_sid: string | null;
  conversation_id: number | null;
  customer_phone: string | null;
  customer_name: string | null;
  agent_number: string | null;
  created_at: string;
  updated_at: string;
  read_at: string | null;
  resolved_at: string | null;
}

export interface NotificationsResponse {
  items: StaffNotification[];
  unread_count: number;
}

export interface MarkReadResponse {
  status: string;
  unread_count: number;
}

/** Payload of the `notification.read` live event. */
export interface NotificationReadEvent {
  ids: number[] | null;
  all: boolean;
}

export type MarkReadTarget = { ids: number[] } | { all: true };

export const DEFAULT_NOTIFICATION_LIMIT = 30;

/** Urgent per payload, falling back to the kind for an older payload without the flag. */
export function isUrgentNotification(item: Pick<StaffNotification, 'urgent' | 'kind'>): boolean {
  return typeof item.urgent === 'boolean' ? item.urgent : URGENT_NOTIFICATION_KINDS.has(item.kind);
}

class NotificationService {
  async list(limit: number = DEFAULT_NOTIFICATION_LIMIT): Promise<NotificationsResponse> {
    return apiService.get<NotificationsResponse>(`/notifications?limit=${encodeURIComponent(String(limit))}`);
  }

  async markRead(target: MarkReadTarget): Promise<MarkReadResponse> {
    return apiService.post<MarkReadResponse>('/notifications/read', target);
  }
}

export const notificationService = new NotificationService();
export default notificationService;
